/**
 * Help overlay: a keyboard-shortcut cheat sheet plus an accordion FAQ. Opened
 * with "?" (or Shift+/) and from the toolbar. Fully inline and offline —
 * no network, no external assets. Focus-trapped via the shared openModal helper.
 */
import { el } from "./app";
import { openModal, type ModalHandle } from "./about";

interface Shortcut {
  keys: string;
  action: string;
}

const SHORTCUTS: Shortcut[] = [
  { keys: "?", action: "Open this help" },
  { keys: "Ctrl / ⌘ + 0", action: "Fit the preview to the window" },
  { keys: "Ctrl / ⌘ + 1", action: "Reset the view to 100%" },
  { keys: "+ / −", action: "Zoom the preview in / out" },
  { keys: "C", action: "Compare with the original (before / after)" },
  { keys: "Ctrl / ⌘ + V", action: "Paste an image from the clipboard" },
  { keys: "Esc", action: "Close a dialog or the settings panel" },
  { keys: "Two-finger pinch", action: "Zoom on a touchscreen" },
  { keys: "Drag", action: "Pan the preview" },
];

interface Faq {
  q: string;
  a: string;
}

const FAQ: Faq[] = [
  {
    q: "Do my files get uploaded anywhere?",
    a: "No. Every step — decoding, pixelating, color separation, and export — runs inside your browser. Nothing is sent to a server, there are no accounts, and there is no tracking.",
  },
  {
    q: "Why do the preset colors not match my machine?",
    a: "MOPA marking colors depend on your exact machine power, lens, focus offset, and the metal itself. Presets are starting points. Use the Calibration Tools tab to burn a test grid, then record the parameters that produced the colors you like.",
  },
  {
    q: "What is pulse width (Q-pulse)?",
    a: "Pulse width — often called the Q-pulse setting, measured in nanoseconds — controls how long each laser pulse lasts. On a MOPA source it is one of the strongest levers for producing different oxide colors on steel.",
  },
  {
    q: "What does 'trace edges' do?",
    a: "It replaces the dots that straddle a shape's outline with exact clipped pieces, so the edge of your artwork stays crisp instead of stair-stepping. Turn it off for a plain dot grid.",
  },
  {
    q: "Which files can I open?",
    a: "LightBurn projects (.lbrn and .lbrn2) and SVG for the vector workflow, and common images (PNG, JPG, WEBP, GIF, BMP, or SVG) for the photo workflow. Older LightBurn files still open.",
  },
  {
    q: "Can I use inches instead of millimeters?",
    a: "Yes. Use the units toggle in the header — every dimension field and readout switches between millimeters and inches. You can also type decimals with a comma (0,25) or a period (0.25).",
  },
  {
    q: "Does it work offline?",
    a: "Yes. Once loaded, the tool keeps working with the network disconnected. That is the same reason your files stay private.",
  },
];

export function openHelpModal(returnFocusTo?: HTMLElement | null): ModalHandle {
  return openModal(
    () => {
      const title = el("h2", { class: "modal-title", attrs: { id: "help-title" }, text: "Help & keyboard shortcuts" });

      const body = el("div", { class: "help-body" });

      // --- shortcuts ---
      const shortcutsSection = el("section", { class: "help-shortcuts" }, [
        el("h3", { text: "Keyboard & touch" }),
      ]);
      const table = el("table", { class: "shortcut-table" });
      for (const s of SHORTCUTS) {
        const row = el("tr", {}, [
          el("th", { attrs: { scope: "row" } }, [el("kbd", { text: s.keys })]),
          el("td", { text: s.action }),
        ]);
        table.append(row);
      }
      shortcutsSection.append(table);

      // --- FAQ accordion ---
      const faqSection = el("section", { class: "help-faq" }, [el("h3", { text: "Frequently asked" })]);
      for (const item of FAQ) {
        const details = el("details", { class: "faq-item" });
        const summary = el("summary", { text: item.q });
        details.append(summary, el("p", { text: item.a }));
        faqSection.append(details);
      }

      body.append(shortcutsSection, faqSection);
      return { title, body };
    },
    { titleId: "help-title", labelledBy: "help-title", cardClass: "help-card", returnFocusTo },
  );
}
